import {
  BaseClass,
  IGraph,
  IGroupBoundsCalculator,
  INode,
  INodeSizeConstraintProvider,
  Rect,
  Size,
  TextRenderSupport,
} from '@ardoq/yfiles';
import { logError } from '@ardoq/logging';
import {
  CollapsibleGraphGroup,
  GROUP_HEADER_FONT_HEIGHT,
  TEXT_PADDING_VERTICAL,
} from '@ardoq/graph';
import { MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT } from '@ardoq/graph-labeling';
import { GRAPH_ITEM_LABEL_CACHE_SIZE } from '@ardoq/global-consts';
import { DiffType } from '@ardoq/data-model';
import { createFifoCache } from '@ardoq/common-helpers';
import {
  BLOCK_DIAGRAM_GROUP_LABEL_TEXT_PARAMS,
  GROUP_PADDING,
  PREFERRED_MINIMUM_GROUP_WIDTH,
  SUBHEADER_FONT_HEIGHT,
} from '../consts';
import {
  determineImgStyleSize,
  determineStyleSize,
} from 'yfilesExtensions/styles/util';
import { isContextNode } from 'yfilesExtensions/styles/nodeDecorator';
import {
  CONTEXT_HIGHLIGHT_PADDING,
  HALO_PADDING,
  NODE_HEIGHT,
  NODE_WIDTH,
} from 'yfilesExtensions/styles/consts';
import { getNodeLayoutSize } from 'yfilesExtensions/getNodeLayoutSize';
import { GraphItem } from 'graph/GraphItem';
import {
  getGroupHeaderWidthWithoutLabel,
  measureGroupLabel,
  measureGroupSubLabel,
  getGroupLabelMaxWidth,
} from '../utils';

/** @returns the bounds of a CollapsibleGraphGroup in classic Block Diagram, based on the group's existing layout and some label measurements. */
export const groupBounds = (groupNode: INode) => {
  const { tag: group, layout } = groupNode;
  if (!(group instanceof CollapsibleGraphGroup)) {
    logError(Error('Invalid node in GroupBoundsCalculator.'));
    return Rect.EMPTY;
  }
  const isComponent = group.isComponent();

  const groupLabels = group.getItemLabels();

  if (!groupLabels) {
    return new Rect(layout.x, layout.y, layout.width, 0);
  }

  const { subLabel, mainLabel } = groupLabels;

  const headerLabelIndentX = getGroupHeaderWidthWithoutLabel(group);

  const groupLabelWidthWithSpacing =
    measureGroupLabel(mainLabel || '') + headerLabelIndentX + GROUP_PADDING;
  const groupSublabelWidthWithSpacing = measureGroupSubLabel(subLabel || '');

  const minimumGroupWidth = Math.min(
    PREFERRED_MINIMUM_GROUP_WIDTH,
    Math.max(groupLabelWidthWithSpacing, groupSublabelWidthWithSpacing)
  );

  if (!group.collapsed) {
    // this is what defines the position of child nodes in respect to the label

    const { groupLabelHeight, subLabelHeight, legacyFormattingHeight } =
      getGroupLabelDimensions(groupNode);

    const groupLabelHeightTotal =
      groupLabelHeight +
      subLabelHeight +
      legacyFormattingHeight +
      subLabelHeight +
      GROUP_HEADER_FONT_HEIGHT;

    return new Rect(
      layout.x,
      layout.y,
      Math.max(layout.width, minimumGroupWidth),
      groupLabelHeightTotal
    );
  }

  if (isComponent) {
    const { width: styleWidth, height: styleHeight } = group.getImage()
      ? determineImgStyleSize(group.getImage())
      : determineStyleSize(`#${group.getShape()}`);

    if (styleWidth <= 0 || styleHeight <= 0) {
      // this can happen if there's a component with no image and no shape. this seems to be an invalid configuration, but it is possible to configure a component type this way. in this case we'll just give it a default size.
      return new Rect(layout.x, layout.y, NODE_WIDTH, NODE_HEIGHT);
    }

    const yAspectRatio = NODE_HEIGHT / styleHeight;
    const xAspectRatio = NODE_WIDTH / styleWidth;

    let width;
    let height;

    if (styleWidth * yAspectRatio > NODE_WIDTH) {
      width = NODE_WIDTH;
      height = styleHeight * xAspectRatio;
    } else {
      width = styleWidth * yAspectRatio;
      height = NODE_HEIGHT;
    }
    const hasContextHighlight = isContextNode(groupNode);
    const nodeSize = getNodeLayoutSize({
      styleWidth: width,
      styleHeight: height,
      hasContextHighlight,
      contextHighlightPadding: CONTEXT_HIGHLIGHT_PADDING,
      visualDiffType:
        groupNode.tag instanceof GraphItem
          ? groupNode.tag.getVisualDiffType()
          : DiffType.NONE,
    });
    return new Rect(
      groupNode.layout.topLeft,
      hasContextHighlight
        ? nodeSize
        : new Size(
            nodeSize.width + 2 * HALO_PADDING,
            nodeSize.height + 2 * HALO_PADDING
          )
    );
  }

  const { groupLabelHeight } = getGroupLabelDimensions(groupNode);

  return new Rect(
    layout.x,
    layout.y,
    Math.max(layout.width, minimumGroupWidth),
    groupLabelHeight + TEXT_PADDING_VERTICAL / 2
  );
};

export class GroupBoundsCalculator extends BaseClass(
  IGroupBoundsCalculator,
  INodeSizeConstraintProvider
) {
  constructor(private groupNode: INode) {
    super();
  }
  override getMaximumSize(): Size {
    return Size.INFINITE;
  }
  override getMinimumEnclosedArea(): Rect {
    return Rect.EMPTY;
  }
  override getMinimumSize(): Size {
    return groupBounds(this.groupNode).size;
  }

  override calculateBounds(_: IGraph): Rect {
    return groupBounds(this.groupNode);
  }
}

const getGroupLabelHeightFromCache = createFifoCache(
  GRAPH_ITEM_LABEL_CACHE_SIZE,
  (key: string) => {
    const { text, width } = JSON.parse(key);

    return TextRenderSupport.measureText({
      text,
      maximumSize: new Size(width || 0, Infinity),
      ...BLOCK_DIAGRAM_GROUP_LABEL_TEXT_PARAMS,
    });
  }
);

const getGroupLabelHeightWithinBlock = (text: string, width: number) => {
  if (!text) {
    return 0;
  }

  const { height } = getGroupLabelHeightFromCache(
    JSON.stringify({
      text,
      width,
    })
  );

  return height;
};

export const getGroupLabelDimensions = (groupNode: INode | null) => {
  const defaultResult = {
    groupLabelWidth: 0,
    groupLabelHeight: 0,
    groupNameMaxHeight: 0,
    legacyFormattingHeight: 0,
    subLabelHeight: 0,
  };

  if (!groupNode) {
    return defaultResult;
  }

  const group = groupNode.tag as CollapsibleGraphGroup;
  const { mainLabel, legacyLabelParts, subLabel } = group.getItemLabels() ?? {};

  const isLegacyFormatting = legacyLabelParts?.fieldValue;

  const groupNameMaxHeight = getMaxGroupLabelHeight(
    Boolean(isLegacyFormatting),
    false
  );

  const legacyFormattingHeight = isLegacyFormatting
    ? LEGACY_FORMATTING_VALUE_HEIGHT_GROUP
    : 0;

  // includes the height of the sublabel and the top margin to the label
  const subLabelHeight = subLabel ? SUBHEADER_FONT_HEIGHT * 2 : 0;

  const groupLabelWidth = getGroupLabelMaxWidth(groupNode);
  const groupLabelHeight = Math.min(
    groupNameMaxHeight,
    getGroupLabelHeightWithinBlock(mainLabel ?? '', groupLabelWidth)
  );

  return {
    groupLabelWidth,
    groupLabelHeight,
    groupNameMaxHeight,
    legacyFormattingHeight,
    subLabelHeight,
  };
};

const LEGACY_FORMATTING_VALUE_HEIGHT_GROUP = getGroupLabelHeightWithinBlock(
  '[]',
  Infinity
);

const THREE_LINES_GROUP_LABEL_HEIGHT = getGroupLabelHeightWithinBlock(
  Array(2).fill('1\r\n').join(''),
  Infinity
);

const FOUR_LINES_GROUP_LABEL_HEIGHT = getGroupLabelHeightWithinBlock(
  Array(3).fill('1\r\n').join(''),
  Infinity
);

export const getMaxGroupLabelHeight = (
  hasLegacyFormatting: boolean,
  isModern: boolean
) =>
  isModern
    ? 2 * MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT
    : hasLegacyFormatting
      ? THREE_LINES_GROUP_LABEL_HEIGHT
      : FOUR_LINES_GROUP_LABEL_HEIGHT;
